import { createClient } from '@supabase/supabase-js';
import * as pdfjsLib from 'pdfjs-dist';
import { DocumentAuth } from './auth.js';
import { initializeTracking } from './tracking.js';

// Initialize PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.js',
  import.meta.url
).toString();

// Initialize Supabase client with error handling
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables:', {
    url: supabaseUrl ? 'set' : 'missing',
    key: supabaseAnonKey ? 'set' : 'missing'
  });
  document.getElementById('loading').classList.add('hidden');
  document.getElementById('error').classList.remove('hidden');
  document.getElementById('error').textContent = 'Configuration error: Missing Supabase credentials';
  throw new Error('Missing Supabase environment variables');
}

// Ensure URL has proper protocol and format
const fullSupabaseUrl = supabaseUrl.startsWith('http') ? supabaseUrl : `https://${supabaseUrl}`;
const supabase = createClient(fullSupabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true
  }
});

// Helper function to parse document identifier from URL
function parseDocumentIdentifier() {
  // First check query parameters (legacy format)
  const urlParams = new URLSearchParams(window.location.search);
  const docId = urlParams.get('doc');
  
  if (docId) {
    return { id: docId, type: 'uuid' };
  }
  
  // Check URL path for new formats
  const pathParts = window.location.pathname.split('/').filter(part => part.length > 0);
  
  // Format: /view/{shortId}
  if (pathParts.length === 2 && pathParts[0] === 'view') {
    return { id: pathParts[1], type: 'short_id' };
  }
  
  // Format: /{userSlug}/{vanitySlug}
  if (pathParts.length === 2 && pathParts[0] !== 'view') {
    return { 
      userSlug: pathParts[0], 
      vanitySlug: pathParts[1],
      type: 'vanity'
    };
  }
  
  return null;
}

class PDFViewer {
  constructor() {
    this.pdfDoc = null;
    this.pageNum = 1;
    this.pageRendering = false;
    this.pageNumPending = null;
    this.scale = this.calculateInitialScale();
    this.docMetadata = null; // Store document metadata
    this.lastTouchDistance = 0;
    this.isMobileDevice = this.checkIfMobile();

    // --- Element References ---
    this.viewerContainer = document.getElementById('viewer-container');
    this.loadingIndicator = document.getElementById('loading');
    this.errorIndicator = document.getElementById('error');
    
    // Header controls
    this.documentControls = document.getElementById('document-controls');
    
    // Page counters (separate for each view mode)
    this.currentPageDisplay = document.getElementById('current_page');
    this.totalPagesDisplay = document.getElementById('total_pages');
    this.pageCounter = document.getElementById('page-counter');
    this.scrollCurrentPageDisplay = document.getElementById('scroll_current_page');
    this.scrollTotalPagesDisplay = document.getElementById('scroll_total_pages');
    this.scrollPageCounter = document.getElementById('scroll-page-counter');
    
    // Paginate Mode Elements
    this.paginationControls = document.getElementById('pagination-controls');
    this.canvasContainer = document.getElementById('canvas-container');
    this.canvas = document.getElementById('pdf-canvas');
    this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
    
    // Scroll Mode Elements
    this.scrollContainer = document.getElementById('scroll-container');
    this.scrollObserver = null; // Will hold IntersectionObserver for tracking visible pages in scroll mode

    this.auth = new DocumentAuth();

    this.setupControls(); // Setup controls for paginate mode
    this.setupTouchEvents(); // Setup touch events for mobile
    this.setupAuthListeners();
  }

  checkIfMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  calculateInitialScale() {
    // Start with smaller scale on mobile devices
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      // Use a scale that makes the content fit the screen width
      const viewportWidth = window.innerWidth;
      // We'll adjust this further once we have the PDF
      return 0.8; // Start with a reasonable scale for mobile
    }
    return 1.5; // Default scale for desktop
  }

  setupControls() {
    // Only setup if controls exist (for paginate mode)
    document.getElementById('prev')?.addEventListener('click', () => this.onPrevPage());
    document.getElementById('next')?.addEventListener('click', () => this.onNextPage());
    document.getElementById('zoomIn')?.addEventListener('click', () => this.changeScale(1.2));
    document.getElementById('zoomOut')?.addEventListener('click', () => this.changeScale(1 / 1.2));
    
    // Add keyboard navigation for paginate mode
    document.addEventListener('keydown', (e) => {
      // Only handle keyboard navigation when in paginate mode and document is loaded
      if (this.pdfDoc && this.docMetadata?.view_mode === 'paginate') {
        if (e.key === 'ArrowLeft') {
          this.onPrevPage();
        } else if (e.key === 'ArrowRight') {
          this.onNextPage();
        }
      }
    });
  }

  setupTouchEvents() {
    if (!this.canvas) return;

    // Touch variables for swipe detection
    let touchStartX = 0;
    let touchEndX = 0;
    let touchStartTime = 0;
    let hasMoved = false; // Track if finger has moved during touch

    // For pinch zoom
    this.canvas.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartTime = new Date().getTime();
      hasMoved = false;
      
      if (e.touches.length === 2) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        this.lastTouchDistance = Math.hypot(
          touch2.pageX - touch1.pageX,
          touch2.pageY - touch1.pageY
        );
      }
    }, { passive: true });
    
    this.canvas.addEventListener('touchmove', (e) => {
      hasMoved = true; // Flag that movement occurred during this touch
      
      if (e.touches.length === 2) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const currentDistance = Math.hypot(
          touch2.pageX - touch1.pageX,
          touch2.pageY - touch1.pageY
        );
        
        if (this.lastTouchDistance > 0) {
          const factor = currentDistance / this.lastTouchDistance;
          if (factor > 1.02 || factor < 0.98) { // Add threshold to avoid tiny changes
            this.changeScale(factor);
            this.lastTouchDistance = currentDistance;
          }
        }
      }
    }, { passive: false });

    this.canvas.addEventListener('touchend', (e) => {
      // Skip processing if this is in a tap zone
      const target = e.target;
      if (target.classList && (target.classList.contains('tap-zone'))) {
        return; // Let the tap zone handle this event
      }
      
      if (this.docMetadata?.view_mode === 'paginate' && hasMoved) {
        touchEndX = e.changedTouches[0].clientX;
        const touchEndTime = new Date().getTime();
        const touchTime = touchEndTime - touchStartTime;
        
        // Only process swipes, not taps (to avoid conflict with tap zones)
        const distance = touchEndX - touchStartX;
        const isSwipe = Math.abs(distance) > 50 && touchTime < 300;
        
        if (isSwipe) {
          if (distance > 0) {
            // Swipe right (previous page)
            console.log("Canvas swipe right detected");
            this.onPrevPage();
          } else {
            // Swipe left (next page)
            console.log("Canvas swipe left detected");
            this.onNextPage();
          }
        }
      }
      
      this.lastTouchDistance = 0;
    }, { passive: true });

    // Add touch events for scroll container too
    this.scrollContainer.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        this.lastTouchDistance = Math.hypot(
          touch2.pageX - touch1.pageX,
          touch2.pageY - touch1.pageY
        );
      }
    }, { passive: false });

    this.scrollContainer.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const currentDistance = Math.hypot(
          touch2.pageX - touch1.pageX,
          touch2.pageY - touch1.pageY
        );
        
        if (this.lastTouchDistance > 0) {
          const factor = currentDistance / this.lastTouchDistance;
          if (factor > 1.02 || factor < 0.98) {
            this.changeScale(factor);
            this.lastTouchDistance = currentDistance;
          }
        }
      }
    }, { passive: false });

    this.scrollContainer.addEventListener('touchend', () => {
      this.lastTouchDistance = 0;
    });
  }

  changeScale(factor) {
      this.scale *= factor;
      // Add min/max limits to prevent zooming out too far or in too much
      this.scale = Math.max(0.5, Math.min(this.scale, 3.0));
      
      if (this.docMetadata.view_mode === 'paginate') {
          this.queueRenderPage(this.pageNum);
      } else if (this.docMetadata.view_mode === 'scroll') {
          // Re-render all pages in scroll mode with new scale
          this.renderScrollablePages();
      }
  }

  setupAuthListeners() {
    // Listen for successful password-only auth
    window.addEventListener('passwordVerified', (event) => {
      console.log("'passwordVerified' event received, loading document...");
      this.loadDocument(event.detail.docId);
    });

    // Listen for successful email+OTP auth (or password+OTP)
    window.addEventListener('authVerified', (event) => {
        console.log("'authVerified' event received, loading document...");
        this.loadDocument(event.detail.docId);
    });
  }

  async findDocumentByIdentifier(identifier) {
    try {
      let query;
      
      if (identifier.type === 'uuid') {
        // UUID format - use the existing query
        query = supabase
          .from('documents')
          .select('*, view_mode')
          .eq('id', identifier.id)
          .single();
      } 
      else if (identifier.type === 'short_id') {
        // Short ID format
        query = supabase
          .from('documents')
          .select('*, view_mode')
          .eq('short_id', identifier.id)
          .single();
      } 
      else if (identifier.type === 'vanity') {
        // Vanity slug format
        query = supabase
          .from('documents')
          .select('*, view_mode')
          .eq('user_slug', identifier.userSlug)
          .eq('vanity_slug', identifier.vanitySlug)
          .single();
      }
      
      const { data: doc, error } = await query;
      
      if (error) {
        console.error('Error finding document:', error);
        return null;
      }
      
      return doc;
    } catch (error) {
      console.error('Exception finding document:', error);
      return null;
    }
  }

  async loadDocument(docIdentifier) {
    try {
      this.loadingIndicator.classList.remove('hidden'); // Show loading
      this.viewerContainer.classList.add('hidden');  // Hide viewer during load
      this.errorIndicator.classList.add('hidden');
      document.getElementById('homepage').classList.add('hidden');
      this.documentControls.classList.add('hidden'); // Hide controls during load

      // If we received a string (legacy format), convert it to an identifier object
      const identifier = typeof docIdentifier === 'string' 
        ? { id: docIdentifier, type: 'uuid' } 
        : docIdentifier;

      // Find the document based on the identifier type
      const doc = await this.findDocumentByIdentifier(identifier);
      
      if (!doc) {
        const idDisplay = identifier.type === 'uuid' ? identifier.id : 
                         identifier.type === 'short_id' ? identifier.id :
                         `${identifier.userSlug}/${identifier.vanitySlug}`;
                         
        this.showHomepage(`Document not found: ${idDisplay}`);
        return;
      }

      const accessResult = await this.auth.checkDocumentAccess(doc.id);
      if (!accessResult.authorized) {
        this.loadingIndicator.classList.add('hidden');
        return;
      }

      this.docMetadata = doc; // Store metadata
      console.log('Document metadata:', doc);

      const { data: pdfData, error: storageError } = await supabase
        .storage
        .from('documents')
        .download(doc.storage_path);

      if (storageError) {
        console.error('Storage error:', storageError);
        throw new Error('Could not download the PDF file');
      }

      const arrayBuffer = await pdfData.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      this.pdfDoc = await loadingTask.promise;

      // Show controls in header
      this.documentControls.classList.remove('hidden');
      
      // Set total page count for both document types
      this.totalPagesDisplay.textContent = this.pdfDoc.numPages;
      this.currentPageDisplay.textContent = '1'; // Start at page 1

      // --- Render based on View Mode --- 
      this.viewerContainer.classList.remove('hidden'); // Show the main viewer area
      if (doc.view_mode === 'scroll') {
          // Hide pagination controls, show scroll page counter
          this.paginationControls.classList.add('hidden');
          this.pageCounter.classList.add('hidden');
          this.scrollPageCounter.classList.remove('hidden');
          this.canvasContainer.classList.add('hidden');
          this.scrollContainer.classList.remove('hidden');
          
          // Set total pages for scroll mode counter
          this.scrollTotalPagesDisplay.textContent = this.pdfDoc.numPages;
          this.scrollCurrentPageDisplay.textContent = '1';
          
          await this.renderScrollablePages();
          this.setupScrollObserver(); // Set up observer to track visible pages in scroll mode
      } else { // Default to paginate
          this.scrollContainer.classList.add('hidden');
          this.scrollPageCounter.classList.add('hidden');
          this.pageCounter.classList.remove('hidden');
          this.canvasContainer.classList.remove('hidden');
          this.paginationControls.classList.remove('hidden');
          
          // Set total pages for paginate mode counter
          this.totalPagesDisplay.textContent = this.pdfDoc.numPages;
          this.currentPageDisplay.textContent = '1';
          
          this.renderPaginatedPage(this.pageNum);
      }
      // --- End View Mode Render ---

      this.loadingIndicator.classList.add('hidden');

      // Initialize Tracking AFTER rendering is complete (or at least started)
      initializeTracking(doc.id, doc.view_mode);

    } catch (error) {
      console.error('Error loading document:', error);
      this.loadingIndicator.classList.add('hidden');
      this.showHomepage(error.message || 'Error loading document');
    }
  }

  // --- Paginate Mode Rendering ---
  async renderPaginatedPage(num) {
    this.pageRendering = true;
    try {
      const page = await this.pdfDoc.getPage(num);
      
      // Adjust scale to fit page width for mobile if needed
      if (this.isMobileDevice && num === 1) {
        const viewport = page.getViewport({ scale: 1.0 });
        const containerWidth = this.canvasContainer.clientWidth - 40; // Account for padding
        const scaleFactor = containerWidth / viewport.width;
        if (scaleFactor < this.scale) {
          this.scale = scaleFactor;
        }
      }
      
      // Get device pixel ratio for high-DPI displays
      const pixelRatio = window.devicePixelRatio || 1;
      
      // Create viewport with the base scale
      const viewport = page.getViewport({ scale: this.scale });
      
      // Set canvas dimensions accounting for the pixel ratio
      this.canvas.height = viewport.height * pixelRatio;
      this.canvas.width = viewport.width * pixelRatio;
      
      // Set display size of the canvas
      this.canvas.style.height = `${viewport.height}px`;
      this.canvas.style.width = `${viewport.width}px`;
      
      // Scale the context for high-DPI rendering
      this.ctx.scale(pixelRatio, pixelRatio);
      
      const renderContext = { 
        canvasContext: this.ctx, 
        viewport: viewport
      };
      
      await page.render(renderContext).promise;
      this.pageRendering = false;
      
      // Update page counter using the new method
      this.updateCurrentPage(num);

      if (this.pageNumPending !== null) {
        this.renderPaginatedPage(this.pageNumPending);
        this.pageNumPending = null;
      }
      
      // Setup direct zone taps for mobile if we're in paginate mode
      if (this.isMobileDevice && this.docMetadata?.view_mode === 'paginate') {
        this.setupTapZones();
      }
    } catch (error) {
      console.error('Error rendering paginated page:', error);
      this.pageRendering = false;
      this.errorIndicator.classList.remove('hidden');
      this.errorIndicator.textContent = 'Error rendering page';
    }
  }

  setupTapZones() {
    const leftZone = document.querySelector('.tap-zone.left');
    const rightZone = document.querySelector('.tap-zone.right');
    const canvasContainer = document.getElementById('canvas-container');
    
    if (!leftZone || !rightZone || !canvasContainer) return;
    
    // First, remove any existing listeners to avoid duplicates
    if (this.handlePrevPage) {
      leftZone.removeEventListener('click', this.handlePrevPage);
      leftZone.removeEventListener('touchend', this.handlePrevPageTouch);
    }
    
    if (this.handleNextPage) {
      rightZone.removeEventListener('click', this.handleNextPage);
      rightZone.removeEventListener('touchend', this.handleNextPageTouch);
    }
    
    if (this.handleCanvasTouch) {
      this.canvas.removeEventListener('touchend', this.handleCanvasTouch);
    }
    
    // Define handler functions properly
    this.handlePrevPage = () => {
      console.log("Left zone clicked");
      this.onPrevPage();
    };
    
    this.handleNextPage = () => {
      console.log("Right zone clicked");
      this.onNextPage();
    };
    
    this.handlePrevPageTouch = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log("Left zone touched");
      this.onPrevPage();
    };
    
    this.handleNextPageTouch = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log("Right zone touched");
      this.onNextPage();
    };
    
    // Show tap zones handler
    this.showTapZones = () => {
      canvasContainer.classList.add('tap-zone-visible');
      if (this.tapZoneTimeout) {
        clearTimeout(this.tapZoneTimeout);
      }
      this.tapZoneTimeout = setTimeout(() => {
        canvasContainer.classList.remove('tap-zone-visible');
      }, 3000); // Show zones for 3 seconds
    };
    
    // Handle single tap on canvas to show tap zones
    this.handleCanvasTouch = (e) => {
      // Only handle if this is a simple tap (not a pinch or swipe)
      if (e.changedTouches.length === 1 && !e.target.classList.contains('tap-zone')) {
        this.showTapZones();
      }
    };
    
    // Add listeners
    leftZone.addEventListener('click', this.handlePrevPage);
    rightZone.addEventListener('click', this.handleNextPage);
    
    // Add touch events
    leftZone.addEventListener('touchend', this.handlePrevPageTouch);
    rightZone.addEventListener('touchend', this.handleNextPageTouch);
    this.canvas.addEventListener('touchend', this.handleCanvasTouch);
    
    // Show tap zones initially
    this.showTapZones();
  }

  queueRenderPage(num) { // Used by paginate controls
    if (this.pageRendering) {
      this.pageNumPending = num;
    } else {
      this.renderPaginatedPage(num);
    }
  }

  onPrevPage() {
    if (this.pageNum <= 1) return;
    this.pageNum -= 1;
    this.queueRenderPage(this.pageNum);
    console.log('Navigate to previous page:', this.pageNum);
  }

  onNextPage() {
    if (this.pageNum >= this.pdfDoc.numPages) return;
    this.pageNum += 1;
    this.queueRenderPage(this.pageNum);
    console.log('Navigate to next page:', this.pageNum);
  }
  // --- End Paginate Mode Rendering ---


  // --- Scroll Mode Rendering and Page Tracking ---
  async renderScrollablePages() {
    try {
      // Clear previous pages
      this.scrollContainer.innerHTML = '';
      
      // If this is the first render on mobile, adjust scale to fit page width
      if (this.isMobileDevice) {
        const firstPage = await this.pdfDoc.getPage(1);
        const viewport = firstPage.getViewport({ scale: 1.0 });
        const containerWidth = this.scrollContainer.clientWidth - 40; // Account for padding
        const scaleFactor = containerWidth / viewport.width;
        if (scaleFactor < this.scale) {
          this.scale = scaleFactor;
        }
      }

      // Get device pixel ratio for high-DPI displays
      const pixelRatio = window.devicePixelRatio || 1;

      // Render each page
      for (let pageNum = 1; pageNum <= this.pdfDoc.numPages; pageNum++) {
        const page = await this.pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: this.scale });
        
        // Create container for this page
        const pageContainer = document.createElement('div');
        pageContainer.className = 'pdf-page';
        pageContainer.dataset.pageNumber = pageNum;
        
        // Create canvas for this page
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas dimensions accounting for pixel ratio
        canvas.height = viewport.height * pixelRatio;
        canvas.width = viewport.width * pixelRatio;
        
        // Set display size of the canvas
        canvas.style.height = `${viewport.height}px`;
        canvas.style.width = `${viewport.width}px`;
        
        // Scale the context for high-DPI rendering
        ctx.scale(pixelRatio, pixelRatio);
        
        const renderContext = { canvasContext: ctx, viewport: viewport };
        
        // Add canvas to page container
        pageContainer.appendChild(canvas);
        
        // Add page container to scroll container
        this.scrollContainer.appendChild(pageContainer);
        
        // Render the page
        await page.render(renderContext).promise;
      }
      
      // Set scroll container to show the first page
      this.scrollContainer.scrollTop = 0;
      this.scrollCurrentPageDisplay.textContent = '1';
        
    } catch (error) {
      console.error('Error rendering scrollable pages:', error);
      this.errorIndicator.classList.remove('hidden');
      this.errorIndicator.textContent = 'Error rendering pages';
    }
  }

  setupScrollObserver() {
      // Clean up existing observer if any
      if (this.scrollObserver) {
          this.scrollObserver.disconnect();
      }

      // Create a new IntersectionObserver to track which page is most visible
      this.scrollObserver = new IntersectionObserver((entries) => {
          // Find the entry with the highest intersection ratio
          let highestVisibleEntry = null;
          let highestRatio = 0;

          entries.forEach(entry => {
              if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
                  highestRatio = entry.intersectionRatio;
                  highestVisibleEntry = entry;
              }
          });

          // If we found a visible page, update the page counter
          if (highestVisibleEntry) {
              const pageNumber = parseInt(highestVisibleEntry.target.dataset.pageNumber, 10);
              this.updateCurrentPage(pageNumber);
          }
      }, {
          root: this.scrollContainer,
          threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9]
      });

      // Observe all page elements
      const pages = this.scrollContainer.querySelectorAll('.pdf-page');
      pages.forEach(page => {
          this.scrollObserver.observe(page);
      });
  }

  updateCurrentPage(pageNumber) {
    // Update the correct page counter based on view mode
    if (this.docMetadata?.view_mode === 'scroll') {
        this.scrollCurrentPageDisplay.textContent = pageNumber;
    } else {
        this.currentPageDisplay.textContent = pageNumber;
    }
    
    // Dispatch event for tracking with old page number if it changed
    const oldPageNumber = this.pageNum !== pageNumber ? this.pageNum : null;
    document.dispatchEvent(new CustomEvent('vibesend:pagechanged', {
        detail: { newPageNumber: pageNumber, oldPageNumber: oldPageNumber }
    }));
    
    this.pageNum = pageNumber;
  }
  // --- End Scroll Mode Rendering and Page Tracking ---

  showHomepage(errorMessage) {
    this.loadingIndicator.classList.add('hidden');
    
    if (errorMessage) {
      // If there's an error message, display it briefly before showing the homepage
      this.errorIndicator.classList.remove('hidden');
      this.errorIndicator.textContent = errorMessage;
      
      setTimeout(() => {
        this.errorIndicator.classList.add('hidden');
        document.getElementById('homepage').classList.remove('hidden');
      }, 3000); // Show error for 3 seconds before showing homepage
    } else {
      // If no error message, just show the homepage
      document.getElementById('homepage').classList.remove('hidden');
    }
  }
}

// Initialize viewer when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const viewer = new PDFViewer();
  
  const docIdentifier = parseDocumentIdentifier();
  
  if (docIdentifier) {
    viewer.loadDocument(docIdentifier);
  } else {
    // Show homepage when no document identifier is provided
    document.getElementById('loading').classList.add('hidden');
    document.getElementById('error').classList.add('hidden');
    document.getElementById('homepage').classList.remove('hidden');
  }
}); 